import React, { type ReactElement, useMemo } from 'react';

import { Typography } from '@mui/material';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import { useStyles } from 'containers/schedule/styles';

export const useScheduleTabs = (
  awaitingResultLength: number,
): {
  id: string;
  title: string;
  emptyState: {
    title: string;
    text: 'emptyUpcomingMatches.text' | 'emptyCompletedMatches.text' | 'emptyUnscheduledMatches.text';
  };
  isDragAndDrop?: boolean;
  awaitingResultMarkUp?: ReactElement;
}[] => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  return useMemo(
    () => [
      {
        id: 'upcomingMatchesTable',
        title: t('upcomingMatches'),
        emptyState: {
          title: t('emptyUpcomingMatches.title'),
          text: 'emptyUpcomingMatches.text',
        },
      },
      {
        id: 'completedMatchesTable',
        title: t('completedMatches'),
        emptyState: {
          title: t('emptyCompletedMatches.title'),
          text: 'emptyCompletedMatches.text',
        },
        awaitingResultMarkUp: (
          <span className={`${classes.tab} ${classes.tabCompleted}`}>
            <Typography>{t('completedMatches')}</Typography>
            <div className={classes.completedBadge}>
              <Typography>{awaitingResultLength}</Typography>
            </div>
          </span>
        ),
      },
      {
        id: 'unScheduledMatchesTable',
        title: t('unscheduledMatches'),
        emptyState: {
          title: t('emptyUnscheduledMatches.title'),
          text: 'emptyUnscheduledMatches.text',
        },
      },
    ],
    [awaitingResultLength, classes.completedBadge, classes.tab, classes.tabCompleted, t],
  );
};

export const getScheduleChange = ({
  minutes,
  hours,
  newDate,
  direction,
  currentDate,
  localTimeZone,
}: {
  minutes: number;
  hours: number;
  newDate: { value: string };
  direction: string;
  currentDate: { value: string };
  localTimeZone: string;
}) => {
  const selectedDateDay = dayjs(dayjs.tz(currentDate?.value, localTimeZone)).dayOfYear();
  const newDateDay = dayjs(dayjs.tz(newDate.value, localTimeZone)).dayOfYear();
  const totalMinutesChange = hours * 60 + minutes;

  return {
    daysChange: newDateDay - selectedDateDay,
    minutesChange: direction === 'earlier' ? -totalMinutesChange : totalMinutesChange,
  };
};
